import { Download, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardStats } from "@/components/dashboard/DashboardStats";
import { InvoiceTable } from "@/components/dashboard/InvoiceTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input, Select } from "@/components/ui/Field";
import {
  ErrorPanel,
  LoadingPanel,
} from "@/components/ui/Loading";
import { useToast } from "@/components/ui/Toast";
import {
  currencyOptionLabel,
} from "@/domain/billing";
import {
  CURRENCIES,
  type CurrencyCode,
  type DashboardSummary,
  type InvoiceListItem,
  type InvoiceRecord,
} from "@/domain/types";
import { displayStatus } from "@/features/invoices/status";
import { downloadInvoicePdf } from "@/features/pdf/invoice-pdf";
import {
  downloadCombinedReceiptPdf,
  downloadReceiptPdf,
} from "@/features/pdf/receipt-pdf";
import { useAsyncResource } from "@/hooks/useAsyncResource";
import { apiRequest } from "@/lib/api";
import { useAppStore } from "@/lib/app-store";

function summarize(
  invoices: InvoiceListItem[],
): DashboardSummary {
  const summary: DashboardSummary = {
    totalInvoices: invoices.length,
    draftCount: 0,
    sentCount: 0,
    overdueCount: 0,
    paidCount: 0,
    outstandingCentsByCurrency: {},
    paidCentsByCurrency: {},
  };

  for (const invoice of invoices) {
    const status = displayStatus(invoice);

    if (status === "draft") {
      summary.draftCount += 1;
    }

    if (status === "sent") {
      summary.sentCount += 1;
    }

    if (status === "overdue") {
      summary.overdueCount += 1;
    }

    if (status === "paid") {
      summary.paidCount += 1;
    }

    const currency = invoice.currency as CurrencyCode;

    if (status === "paid") {
      summary.paidCentsByCurrency[currency] =
        (summary.paidCentsByCurrency[currency] ?? 0) +
        invoice.totals.totalCents;
    } else if (status !== "void") {
      summary.outstandingCentsByCurrency[currency] =
        (summary.outstandingCentsByCurrency[currency] ??
          0) + invoice.totals.balanceCents;
    }
  }

  return summary;
}

export function DashboardPage() {
  const resource = useAsyncResource(() =>
    apiRequest<InvoiceListItem[]>(
      "/api/admin/invoices",
    ),
  );

  const { showToast } = useToast();

  const search = useAppStore(
    (state) => state.dashboardSearch,
  );

  const status = useAppStore(
    (state) => state.dashboardStatus,
  );

  const currencyFilter = useAppStore(
    (state) => state.dashboardCurrency,
  );

  const setSearch = useAppStore(
    (state) => state.setDashboardSearch,
  );

  const setStatus = useAppStore(
    (state) => state.setDashboardStatus,
  );

  const setCurrencyFilter = useAppStore(
    (state) => state.setDashboardCurrency,
  );

  const [selectedPaidIds, setSelectedPaidIds] =
    useState<Set<string>>(new Set());

  const invoices = resource.data ?? [];

  const filtered = useMemo(
    () =>
      invoices.filter((invoice) => {
        const matchesStatus =
          status === "all" ||
          displayStatus(invoice) === status;

        const matchesCurrency =
          currencyFilter === "all" ||
          invoice.currency === currencyFilter;

        const query = search.trim().toLowerCase();

        const matchesSearch =
          !query ||
          `${invoice.invoiceNumber} ${invoice.clientName} ${invoice.clientCompany} ${invoice.serviceStartDate} ${invoice.serviceEndDate} ${invoice.currency} ${invoice.billingCycle}`
            .toLowerCase()
            .includes(query);

        return (
          matchesStatus &&
          matchesCurrency &&
          matchesSearch
        );
      }),
    [currencyFilter, invoices, search, status],
  );

  const summary = useMemo(
    () => summarize(invoices),
    [invoices],
  );

  const loadInvoice = (id: string) =>
    apiRequest<InvoiceRecord>(
      `/api/admin/invoices/${id}`,
    );

  const downloadInvoice = async (id: string) => {
    try {
      downloadInvoicePdf(await loadInvoice(id));
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "Could not download invoice.",
        "error",
      );
    }
  };

  const downloadReceipt = async (id: string) => {
    try {
      downloadReceiptPdf(await loadInvoice(id));
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "Could not download receipt.",
        "error",
      );
    }
  };

  const downloadCombined = async () => {
    try {
      const records = await Promise.all(
        [...selectedPaidIds].map(loadInvoice),
      );
      downloadCombinedReceiptPdf(records);
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "Could not create the combined receipt.",
        "error",
      );
    }
  };

  const copyLink = async (
    invoice: InvoiceListItem,
  ) => {
    if (!invoice.share.url) {
      return;
    }

    await navigator.clipboard.writeText(
      invoice.share.url,
    );

    showToast("Client link copied.");
  };

  const togglePaid = (id: string) =>
    setSelectedPaidIds((current) => {
      const next = new Set(current);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });

  return (
    <>
      <PageHeader
        eyebrow="Command center"
        title="Invoices & receipts"
        description="Create invoices, track payments, share live read-only client links, and generate professional PDFs."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={downloadCombined}
              disabled={selectedPaidIds.size === 0}
            >
              <Download className="h-4 w-4" />
              Combined receipt ({selectedPaidIds.size})
            </Button>

            <Link
              to="/app/invoices/new"
              className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Plus className="h-4 w-4" />
              Create invoice
            </Link>
          </>
        }
      />

      {resource.loading ? (
        <LoadingPanel label="Loading invoices…" />
      ) : resource.error ? (
        <ErrorPanel
          error={resource.error}
          onRetry={resource.reload}
        />
      ) : (
        <div className="space-y-5">
          <DashboardStats summary={summary} />

          <Card className="p-4">
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_190px_190px]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
                <Input
                  className="pl-10"
                  placeholder="Search invoice, client, currency, or coverage date…"
                  value={search}
                  onChange={(event) =>
                    setSearch(event.target.value)
                  }
                />
              </div>

              <Select
                value={status}
                onChange={(event) =>
                  setStatus(
                    event.target.value as typeof status,
                  )
                }
              >
                <option value="all">All statuses</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="overdue">
                  Overdue
                </option>
                <option value="paid">Paid</option>
                <option value="void">Void</option>
              </Select>

              <Select
                value={currencyFilter}
                onChange={(event) =>
                  setCurrencyFilter(
                    event.target
                      .value as typeof currencyFilter,
                  )
                }
              >
                <option value="all">
                  All currencies
                </option>
                {CURRENCIES.map((currency) => (
                  <option
                    key={currency}
                    value={currency}
                  >
                    {currencyOptionLabel(currency)}
                  </option>
                ))}
              </Select>
            </div>
          </Card>

          <InvoiceTable
            invoices={filtered}
            selectedPaidIds={selectedPaidIds}
            onTogglePaid={togglePaid}
            onCopyLink={copyLink}
            onDownloadInvoice={downloadInvoice}
            onDownloadReceipt={downloadReceipt}
          />
        </div>
      )}
    </>
  );
}
